/**
 * Right-drawer drill-in views for sidebar patient timeline artifacts.
 *
 * Per PRD.180, clicking Forms / Invoices / Files in the sidebar timeline
 * opens a contextual right drawer rather than replacing the main content.
 * The main content patient timeline continues to navigate normally.
 *
 * Each drawer is intentionally lightweight — it surfaces the essentials
 * plus an "Open full page" escape hatch for deeper workflows.
 */
import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon } from './shared';
import { usePowerSyncQuery } from '../adapters/powersync';
import {
  qInvoiceItems,
  qInvoicesByEncounter,
  qPatientFilesByEncounter,
  type InvoiceRow,
  type InvoiceItemRow,
  type PatientFileRow,
} from '@helios/data-access';
import { FormRendererPage } from '../pages/patient/encounters/FormRendererPage';

// ─── Shared helpers ───

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtPhp(cents: number | null): string {
  if (cents == null) return '—';
  return `₱${cents.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const META_ROW_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
};

const META_LABEL_STYLE: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-xs)',
};

// ─── Form drawer ───

export function TimelineFormDrawer({
  open,
  onOpenChange,
  brandTokens,
  patientId,
  encounterId,
  formInstanceId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  patientId: string;
  encounterId: string | null;
  formInstanceId: string | null;
}) {
  const navigate = useNavigate();

  const openFullPage = () => {
    if (!encounterId || !formInstanceId) return;
    onOpenChange(false);
    navigate(`/patient/${patientId}/encounters/${encounterId}/forms/${formInstanceId}`);
  };

  const hasIds = Boolean(patientId && encounterId && formInstanceId);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      // Title is already rendered by the embedded FormRendererPage header.
      title=""
      portalStyle={brandTokens as CSSProperties}
      width="min(900px, 60vw)"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="gradient" size="lg" onClick={openFullPage} disabled={!encounterId}>
            Open Full Page
          </Button>
        </div>
      }
    >
      {hasIds && encounterId && formInstanceId ? (
        <FormRendererPage
          embedded
          overrideIds={{
            patientId,
            encounterId,
            formInstanceId,
          }}
        />
      ) : (
        <div
          style={{
            padding: 'var(--sp-6, 24px)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      )}
    </Drawer>
  );
}

// ─── Invoice drawer ───

export function TimelineInvoiceDrawer({
  open,
  onOpenChange,
  brandTokens,
  patientId,
  encounterId,
  invoiceId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  patientId: string;
  encounterId: string | null;
  invoiceId: string | null;
}) {
  const navigate = useNavigate();

  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId ?? ''),
  );
  const invoice = invoiceRows.find((i) => i.id === invoiceId) ?? null;

  const { data: items = [] } = usePowerSyncQuery<InvoiceItemRow>(qInvoiceItems(invoiceId ?? ''));

  const status = invoice?.status ?? null;
  const statusVariant: 'warning' | 'neutral' = status === 'draft' ? 'warning' : 'neutral';
  const title = invoice?.invoice_no ?? 'Invoice';

  const openFullPage = () => {
    if (!encounterId || !invoiceId) return;
    onOpenChange(false);
    navigate(`/patient/${patientId}/encounters/${encounterId}?tab=invoices&invoiceId=${invoiceId}`);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      badges={
        status ? (
          <DrawerBadge
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            variant={statusVariant}
          />
        ) : null
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="gradient" size="lg" onClick={openFullPage} disabled={!encounterId}>
            Open Full Invoice
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4, 16px)',
        }}
      >
        {!invoice ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Loading…
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2, 8px)',
                padding: 'var(--sp-4, 16px)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface-2, #f9fafb)',
              }}
            >
              <div style={META_ROW_STYLE}>
                <span style={META_LABEL_STYLE}>Date</span>
                <span>{fmtDateTime(invoice.date_iso ?? invoice.created_at)}</span>
              </div>
              {invoice.reference_no && (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Reference</span>
                  <span>{invoice.reference_no}</span>
                </div>
              )}
            </div>

            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 'var(--sp-2, 8px)',
                }}
              >
                Items
              </div>
              {items.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  No items on this invoice.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {items.map((it) => {
                    const qty = it.qty ?? 1;
                    const unit = it.unit_price ?? 0;
                    const lineTotal = qty * unit;
                    return (
                      <div
                        key={it.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 'var(--sp-3)',
                          padding: 'var(--sp-2) 0',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              color: 'var(--text)',
                              fontWeight: 'var(--font-weight-medium)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {it.service_name ?? 'Item'}
                          </div>
                          <div
                            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                          >
                            {qty} × {fmtPhp(unit)}
                          </div>
                        </div>
                        <span
                          style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}
                        >
                          {fmtPhp(lineTotal)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2, 8px)',
                padding: 'var(--sp-4, 16px)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {invoice.subtotal != null && (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Subtotal</span>
                  <span>{fmtPhp(invoice.subtotal)}</span>
                </div>
              )}
              {invoice.discount_total ? (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Discount</span>
                  <span>-{fmtPhp(invoice.discount_total)}</span>
                </div>
              ) : null}
              {invoice.tax_total ? (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Tax</span>
                  <span>{fmtPhp(invoice.tax_total)}</span>
                </div>
              ) : null}
              <div
                style={{
                  ...META_ROW_STYLE,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  paddingTop: 'var(--sp-2)',
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span>Total</span>
                <span>{fmtPhp(invoice.grand_total)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

// ─── File drawer ───

export function TimelineFileDrawer({
  open,
  onOpenChange,
  brandTokens,
  patientId,
  encounterId,
  fileId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  patientId: string;
  encounterId: string | null;
  fileId: string | null;
}) {
  const navigate = useNavigate();

  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId ?? ''),
  );
  const file = fileRows.find((f) => f.id === fileId) ?? null;

  const isImage = file
    ? file.kind === 'image' || (file.mime_type ?? '').startsWith('image/')
    : false;

  const openFullPage = () => {
    if (!encounterId) return;
    onOpenChange(false);
    navigate(`/patient/${patientId}/encounters/${encounterId}?tab=files`);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={file?.filename ?? 'File'}
      portalStyle={brandTokens as CSSProperties}
      badges={file?.tag ? <DrawerBadge label={file.tag} variant="neutral" /> : null}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="gradient" size="lg" onClick={openFullPage} disabled={!encounterId}>
            Open Files Tab
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4, 16px)',
        }}
      >
        {!file ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Loading…
          </div>
        ) : (
          <>
            {/* Preview */}
            {isImage && file.url ? (
              <a href={file.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={file.url}
                  alt={file.filename ?? 'File'}
                  style={{
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'contain',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-surface-2)',
                    display: 'block',
                  }}
                />
              </a>
            ) : (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  gap: 'var(--sp-3)',
                }}
              >
                <NavIcon name="file" />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>{file.filename ?? 'File'}</span>
              </div>
            )}

            {/* Metadata */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2, 8px)',
                padding: 'var(--sp-4, 16px)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface-2, #f9fafb)',
              }}
            >
              {file.mime_type && (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Type</span>
                  <span>{file.mime_type}</span>
                </div>
              )}
              {file.created_at && (
                <div style={META_ROW_STYLE}>
                  <span style={META_LABEL_STYLE}>Uploaded</span>
                  <span>{fmtDateTime(file.created_at)}</span>
                </div>
              )}
            </div>

            {/* Download */}
            {file.url && (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-primary)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                }}
              >
                <NavIcon name="external-link" /> Open file in new tab
              </a>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
